import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import useIsMobile from "../../shared_modules/useIsMobile";
import { apiRequest } from "../../api/api";
import { getSubdomain } from "../../utils/getSubdomain";

const SelectService = ({ barber, selectedService, setSelectedService }) => {
  const isMobile = useIsMobile();
  const domain = getSubdomain();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!barber?.user_id) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiRequest(
          `/guests/services.php?domain=${domain}&barber_id=${barber.user_id}`,
        );
        setServices(res.data.services || []);
      } catch (e) {
        setError(e.message || "Failed to load services.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [barber?.user_id]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 5 }}>
        <CircularProgress size={18} sx={{ color: "text.secondary" }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading services...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 5 }}>
        {error}
      </Alert>
    );
  }

  if (services.length === 0) {
    return (
      <Box sx={{ mt: 5 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No services available for this barber.
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 5 }}>
      {services.map((service) => {
        const isSelected = selectedService?.id === service.id;

        return (
          <Grid key={service.id} size={isMobile ? 6 : 4} sx={{ display: "flex" }}>
            <Box
              component={Paper}
              elevation={isSelected ? 3 : 1}
              onClick={() => setSelectedService(service)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                p: "20px",
                borderRadius: 2,
                border: "0.1px solid",
                borderColor: isSelected ? "primary.main" : "customDivider",
                backgroundColor: isSelected ? "primary.main" : "offWhite",
                cursor: "pointer",
                transition: "all 0.15s ease",
                flex: 1,
                width: "100%",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 3,
                },
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: isSelected ? "primary.contrastText" : "text.primary",
                }}
              >
                {service.name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: isSelected ? "rgba(254,252,247,0.65)" : "text.secondary",
                }}
              >
                {service.duration_minutes} min
              </Typography>
              {service.description ? (
                <Typography
                  variant="caption"
                  sx={{
                    mt: 0.5,
                    color: isSelected
                      ? "rgba(254,252,247,0.5)"
                      : "text.secondary",
                  }}
                >
                  {service.description}
                </Typography>
              ) : null}
              <Typography
                variant="h5"
                sx={{
                  mt: 2,
                  color: isSelected ? "secondary.main" : "text.third",
                }}
              >
                ${Number(service.price).toFixed(2)}
              </Typography>
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default SelectService;
